import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DataTable from "components/common/tables/DataTable";
import { usePageTitle } from "context/PageTitleContext";
import { PERMISSIONS } from "auth/permissionCodes";
import { usePermissions } from "hooks/usePermissions";
import {
  catalogApi,
  catalogConfigs,
  normalizeResponseError,
} from "../../services/catalogs/systemCatalog.service";
import CatalogFormDialog from "./CatalogFormDialog";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import { AppButton } from "components/common/buttons";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import { AppConfirmDialog } from "components/common/dialogs";

const permissionByType = {
  enterprises: { create: PERMISSIONS.SYSTEM.ENTERPRISE_CREATE, update: PERMISSIONS.SYSTEM.ENTERPRISE_UPDATE, delete: PERMISSIONS.SYSTEM.ENTERPRISE_DELETE },
  provinces: { create: PERMISSIONS.SYSTEM.PROVINCE_CREATE, update: PERMISSIONS.SYSTEM.PROVINCE_UPDATE, delete: PERMISSIONS.SYSTEM.PROVINCE_DELETE },
  communes: { create: PERMISSIONS.SYSTEM.COMMUNE_CREATE, update: PERMISSIONS.SYSTEM.COMMUNE_UPDATE, delete: PERMISSIONS.SYSTEM.COMMUNE_DELETE },
  ...Object.fromEntries(["titles", "majors", "qualifications", "certificateTypes"].map((type) => [type, {
    create: PERMISSIONS.SYSTEM.PERSONNEL_CREATE, update: PERMISSIONS.SYSTEM.PERSONNEL_UPDATE, delete: PERMISSIONS.SYSTEM.PERSONNEL_DELETE,
  }])),
  contractGroups: { create: PERMISSIONS.SYSTEM.CONTRACT_GROUP_CREATE, update: PERMISSIONS.SYSTEM.CONTRACT_GROUP_UPDATE, delete: PERMISSIONS.SYSTEM.CONTRACT_GROUP_DELETE },
  contractors: { create: PERMISSIONS.SYSTEM.CONTRACTOR_CREATE, update: PERMISSIONS.SYSTEM.CONTRACTOR_UPDATE, delete: PERMISSIONS.SYSTEM.CONTRACTOR_DELETE },
  clients: { create: PERMISSIONS.SYSTEM.CLIENT_CREATE, update: PERMISSIONS.SYSTEM.CLIENT_UPDATE, delete: PERMISSIONS.SYSTEM.CLIENT_DELETE },
  roleInContracts: { create: PERMISSIONS.SYSTEM.ROLE_IN_CONTRACT_CREATE, update: PERMISSIONS.SYSTEM.ROLE_IN_CONTRACT_UPDATE, delete: PERMISSIONS.SYSTEM.ROLE_IN_CONTRACT_DELETE },
  roleInContract: { create: PERMISSIONS.SYSTEM.ROLE_IN_CONTRACT_CREATE, update: PERMISSIONS.SYSTEM.ROLE_IN_CONTRACT_UPDATE, delete: PERMISSIONS.SYSTEM.ROLE_IN_CONTRACT_DELETE },
  unitInContracts: { create: PERMISSIONS.SYSTEM.UNIT_IN_CONTRACT_CREATE, update: PERMISSIONS.SYSTEM.UNIT_IN_CONTRACT_UPDATE, delete: PERMISSIONS.SYSTEM.UNIT_IN_CONTRACT_DELETE },
  unitInContract: { create: PERMISSIONS.SYSTEM.UNIT_IN_CONTRACT_CREATE, update: PERMISSIONS.SYSTEM.UNIT_IN_CONTRACT_UPDATE, delete: PERMISSIONS.SYSTEM.UNIT_IN_CONTRACT_DELETE },
  roleInSubcontracts: { create: PERMISSIONS.SYSTEM.ROLE_IN_SUBCONTRACT_CREATE, update: PERMISSIONS.SYSTEM.ROLE_IN_SUBCONTRACT_UPDATE, delete: PERMISSIONS.SYSTEM.ROLE_IN_SUBCONTRACT_DELETE },
  roleInSubcontract: { create: PERMISSIONS.SYSTEM.ROLE_IN_SUBCONTRACT_CREATE, update: PERMISSIONS.SYSTEM.ROLE_IN_SUBCONTRACT_UPDATE, delete: PERMISSIONS.SYSTEM.ROLE_IN_SUBCONTRACT_DELETE },
  unitInSubcontracts: { create: PERMISSIONS.SYSTEM.UNIT_IN_SUBCONTRACT_CREATE, update: PERMISSIONS.SYSTEM.UNIT_IN_SUBCONTRACT_UPDATE, delete: PERMISSIONS.SYSTEM.UNIT_IN_SUBCONTRACT_DELETE },
  unitInSubcontract: { create: PERMISSIONS.SYSTEM.UNIT_IN_SUBCONTRACT_CREATE, update: PERMISSIONS.SYSTEM.UNIT_IN_SUBCONTRACT_UPDATE, delete: PERMISSIONS.SYSTEM.UNIT_IN_SUBCONTRACT_DELETE },
};

const CatalogManagement = ({ type, pageTitle }) => {
  const config = catalogConfigs[type];
  const permissions = usePermissions();
  const actionPermissions = permissionByType[type] || {};
  const canCreate = permissions.can(actionPermissions.create);
  const canUpdate = permissions.can(actionPermissions.update);
  const canDelete = permissions.can(actionPermissions.delete);
  const canMutate = canUpdate || canDelete;
  const { setPageTitle } = usePageTitle();
  const [rows, setRows] = useState([]);
  const [lookups, setLookups] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
  const [globalFilter, setGlobalFilter] = useState("");
  const requestVersion = useRef(0);
  const fields = useMemo(() => config.fields.map((field) => field.name === config.lookupKey
    ? { ...field, options: lookups.map((item) => ({ value: item[config.lookupKey], label: item[config.lookupLabel] })) }
    : field), [config, lookups]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formState, setFormState] = useState({ open: false, mode: "create", row: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [duplicateSubmission, setDuplicateSubmission] = useState(null);
  const saveInProgress = useRef(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const loadData = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    try {
      const [result, lookupItems] = await Promise.all([
        config.serverPagination
          ? catalogApi.page(config.endpoint, { pageSize: pagination.pageSize, pageNumber: pagination.pageIndex + 1, searchTerm: globalFilter || undefined })
          : catalogApi.list(config.endpoint, { pageSize: 1000, pageNumber: 1, sortDirection: "ASC" }),
        config.lookupEndpoint ? catalogApi.list(config.lookupEndpoint, { pageSize: 1000, pageNumber: 1 }) : Promise.resolve([]),
      ]);
      if (version !== requestVersion.current) return;
      setLookups(lookupItems);
      setRowCount(result.totalCount ?? 0);
      setRows((config.serverPagination ? result.items : result).map((item) => ({
        ...config.normalize(item),
        ...(config.lookupEndpoint ? { provinceName: lookupItems.find((lookup) => lookup[config.lookupKey] === item[config.lookupKey])?.[config.lookupLabel] ?? "" } : {}),
      })));
    } catch (error) {
      if (version !== requestVersion.current) return;
      setRows([]);
      setLookups([]);
      setRowCount(0);
      showSnackbar(normalizeResponseError(error, `Không thể tải danh sách ${config.title}`), "error");
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [config, showSnackbar, pagination.pageIndex, pagination.pageSize, globalFilter]);

  useEffect(() => {
    setPageTitle(pageTitle);
  }, [pageTitle, setPageTitle]);

  useEffect(() => {
    loadData();
    return () => { requestVersion.current += 1; };
  }, [loadData]);

  const saveSubmission = async ({ payload, mode, id }) => {
    if (mode === "edit") {
      await catalogApi.update(config.endpoint, id, payload);
      showSnackbar(`Cập nhật ${config.title} thành công`);
    } else {
      await catalogApi.create(config.endpoint, payload);
      showSnackbar(`Thêm ${config.title} thành công`);
    }
    setDuplicateSubmission(null);
    setFormState({ open: false, mode: "create", row: null });
    await loadData();
  };

  const handleSubmit = async (values) => {
    if (saveInProgress.current || duplicateSubmission || (formState.mode === "edit" ? !canUpdate : !canCreate)) return;
    saveInProgress.current = true;
    setSubmitting(true);
    try {
      const payload = config.toPayload(values);
      const submission = { payload, mode: formState.mode, id: formState.row?.[config.idKey] };
      if (type === "communes" && await catalogApi.findDuplicateCommune(payload.provinceId, payload.communeName, submission.id)) {
        const provinceName = lookups.find((item) => Number(item.provinceId) === payload.provinceId)?.provinceName;
        setDuplicateSubmission({ ...submission, provinceName });
        return;
      }
      await saveSubmission(submission);
    } catch (error) {
      showSnackbar(normalizeResponseError(error, `Không thể lưu ${config.title}`), "error");
    } finally {
      saveInProgress.current = false;
      setSubmitting(false);
    }
  };

  const handleConfirmDuplicate = async () => {
    if (!duplicateSubmission || saveInProgress.current || (duplicateSubmission.mode === "edit" ? !canUpdate : !canCreate)) return;
    saveInProgress.current = true;
    setSubmitting(true);
    try {
      await saveSubmission(duplicateSubmission);
    } catch (error) {
      showSnackbar(normalizeResponseError(error, `Không thể lưu ${config.title}`), "error");
    } finally {
      saveInProgress.current = false;
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || submitting || !canDelete) return;
    setSubmitting(true);
    try {
      await catalogApi.remove(config.endpoint, deleteTarget[config.idKey]);
      setDeleteTarget(null);
      showSnackbar(`Xóa ${config.title} thành công`);
      await loadData();
    } catch (error) {
      showSnackbar(normalizeResponseError(error, `Không thể xóa ${config.title}`), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo(() => config.columns, [config.columns]);

  return (
    <Box sx={{ p: 2, backgroundColor: "#f9fafb", minHeight: "calc(100vh - 64px)" }}>
      <Stack spacing={2}>
        <DataTable
          columns={columns}
          data={rows}
          enableRowActions={canMutate}
          state={{ isLoading: loading, showProgressBars: submitting, ...(config.serverPagination ? { pagination, globalFilter } : {}) }}
          manualPagination={Boolean(config.serverPagination)}
          manualFiltering={Boolean(config.serverPagination)}
          rowCount={config.serverPagination ? rowCount : undefined}
          onPaginationChange={config.serverPagination ? setPagination : undefined}
          onGlobalFilterChange={config.serverPagination ? (value) => { setGlobalFilter(value); setPagination((current) => ({ ...current, pageIndex: 0 })); } : undefined}
          renderTopToolbarExtraActions={() => canCreate ? (
            <Tooltip title={config.addLabel}>
              <span>
              <AppButton
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setFormState({ open: true, mode: "create", row: null })}
                disableElevation
                disabled={loading || submitting}
              >
                {config.addLabel}
              </AppButton>
              </span>
            </Tooltip>
          ) : null}
          renderRowActions={({ row }) => (
            <Stack direction="row" spacing={0.5} justifyContent="center">
              {canUpdate && <Tooltip title="Sửa">
                <IconButton
                  aria-label="Sửa"
                  size="small"
                  color="primary"
                  onClick={() => setFormState({ open: true, mode: "edit", row: row.original })}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>}
              {canDelete && <Tooltip title="Xóa">
                <IconButton aria-label="Xóa" size="small" color="error" onClick={() => setDeleteTarget(row.original)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>}
            </Stack>
          )}
        />
      </Stack>

      <CatalogFormDialog
        open={formState.open}
        title={formState.mode === "edit" ? `Cập nhật ${config.title}` : config.addLabel}
        fields={fields}
        initialValues={formState.row}
        submitting={submitting}
        onClose={() => { if (!submitting) { setDuplicateSubmission(null); setFormState({ open: false, mode: "create", row: null }); } }}
        onSubmit={handleSubmit}
      />

      <AppConfirmDialog
        open={Boolean(duplicateSubmission)}
        title="Tên phường/xã đã tồn tại"
        type="warning"
        message={duplicateSubmission ? `Tên “${duplicateSubmission.payload.communeName}” đã có trong ${duplicateSubmission.provinceName || "tỉnh/thành phố được chọn"}. Bạn có đồng ý ${duplicateSubmission.mode === "edit" ? "lưu" : "thêm"} bản ghi cùng tên không?` : ""}
        confirmText={duplicateSubmission?.mode === "edit" ? "Đồng ý lưu trùng" : "Đồng ý thêm trùng"}
        cancelText="Quay lại"
        loading={submitting}
        onClose={() => { if (!submitting) setDuplicateSubmission(null); }}
        onConfirm={handleConfirmDuplicate}
      />

      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        submitting={submitting}
        message={`Bạn có chắc chắn muốn xóa ${config.title} này?`}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />

      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      />
    </Box>
  );
};

export default CatalogManagement;
